import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/client';
import { Folder, FileBox, Upload, Radio, AlertTriangle, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export default function Dashboard() {
  const { user } = useAuth();

  const { data: cases, isLoading: casesLoading } = useQuery({
    queryKey: ['cases'],
    queryFn: () => apiClient.getCases(),
  });

  const activeCases = cases?.filter(c => c.status === 'open' || c.status === 'Active') || [];
  const totalEvidence = cases?.reduce((sum, c) => sum + (c.evidenceCount || 0), 0) || 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <Radio className="h-5 w-5 text-rtcc-accent" />
          <h1 className="text-2xl font-bold text-white">Operations Center</h1>
        </div>
        <p className="text-slate-400 text-sm">
          Welcome back, {user?.name}. Real-time evidence monitoring and intake.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Active Cases"
          value={casesLoading ? '...' : activeCases.length}
          icon={<Folder className="h-5 w-5" />}
          color="text-rtcc-info"
          bgColor="bg-rtcc-info/10"
        />
        <StatCard
          label="Total Evidence"
          value={casesLoading ? '...' : totalEvidence}
          icon={<FileBox className="h-5 w-5" />}
          color="text-rtcc-accent"
          bgColor="bg-rtcc-accent/10"
        />
        <StatCard
          label="Pending Review"
          value={casesLoading ? '...' : cases?.filter(c => c.status === 'pending' || c.status === 'Pending Review').length || 0}
          icon={<AlertTriangle className="h-5 w-5" />}
          color="text-rtcc-warning"
          bgColor="bg-rtcc-warning/10"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link
          to="/upload"
          className="flex items-center gap-4 p-5 bg-rtcc-panel rounded-xl border border-slate-700 hover:border-rtcc-accent transition-colors group"
        >
          <div className="p-3 bg-rtcc-accent/10 rounded-lg group-hover:bg-rtcc-accent/20 transition-colors">
            <Upload className="h-6 w-6 text-rtcc-accent" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Upload Evidence</h3>
            <p className="text-slate-400 text-sm">Video, audio, images, documents</p>
          </div>
        </Link>
        <Link
          to="/cases"
          className="flex items-center gap-4 p-5 bg-rtcc-panel rounded-xl border border-slate-700 hover:border-rtcc-info transition-colors group"
        >
          <div className="p-3 bg-rtcc-info/10 rounded-lg group-hover:bg-rtcc-info/20 transition-colors">
            <Folder className="h-6 w-6 text-rtcc-info" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Case Feed</h3>
            <p className="text-slate-400 text-sm">Browse active investigations</p>
          </div>
        </Link>
      </div>

      {/* Recent Cases */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4">Recent Cases</h2>
        {casesLoading ? (
          <div className="text-slate-400 text-sm">Loading...</div>
        ) : activeCases.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Folder className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p>No active cases</p>
          </div>
        ) : (
          <div className="space-y-2">
            {activeCases.slice(0, 8).map((c) => (
              <Link
                key={c.caseNumber}
                to={`/cases/${c.caseNumber}`}
                className="flex items-center justify-between p-4 bg-rtcc-panel rounded-lg border border-slate-700 hover:border-slate-600 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-xs font-mono text-rtcc-accent">{c.caseNumber}</span>
                  <span className="text-sm text-white truncate">{c.title}</span>
                  {c.type && (
                    <span className="text-[10px] px-2 py-0.5 bg-slate-700 text-slate-300 rounded-full">{c.type}</span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500 flex-shrink-0">
                  <span>{c.evidenceCount || 0} items</span>
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatDistanceToNow(new Date(c.createdAt), { addSuffix: true })}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, color, bgColor }: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}) {
  return (
    <div className="p-5 bg-rtcc-panel rounded-xl border border-slate-700">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</span>
        <div className={`p-2 rounded-lg ${bgColor} ${color}`}>{icon}</div>
      </div>
      <p className="text-3xl font-bold text-white">{value}</p>
    </div>
  );
}
